import _get from 'lodash/get';
import { differenceInSeconds } from 'date-fns';

import { MOET_CONSTANTS } from 'moet-core';
import { isRunningContextCypress } from 'moet-test-utils/cypress/utils';

import { APP_PATHS, IS_ONBOARDING_APP } from '@/constants';
import { merchantApplicationFlow } from './merchant-application';
import { preAssessmentNonSmeFlow } from './pre-assessment/non-sme';
import { preAssessmentSmeFlow } from './pre-assessment/sme';
import { enablePayoutsConfig } from './enable-payouts';
import { recollectionFlow } from './recollection';
import signupFlow from './signup';
import {
  celebrationFlow,
  fallbackTo,
  preAssessmentOverviewFlow,
  preAssessmentWrongPrefillFlow,
} from './standalone-fallbacks';
import { getAfterFlowStep } from './merchant-application/common';
import { documentUploadFlow } from './document-upload';
import { merchantVerificationFlow } from './merchant-verification';

const {
  ONBOARDING_STATUS_VALUES_V2,
  CELEBRATION_TIME_WINDOW_CYPRESS_SECONDS,
  CELEBRATION_TIME_WINDOW_SECONDS,
  WIDGET_TYPE,
} = MOET_CONSTANTS;

// time window is in seconds
const celebrationTimeWindow = isRunningContextCypress(window)
  ? CELEBRATION_TIME_WINDOW_CYPRESS_SECONDS
  : CELEBRATION_TIME_WINDOW_SECONDS;

export const FLOW_IDS = {
  SUBMITTED_MANAGED_RISK: `SUBMITTED_${WIDGET_TYPE.MANAGED_RISK}`,
  WRONG_PREFILL_MANAGED_RISK: `WRONG_PREFILL_${WIDGET_TYPE.MANAGED_RISK}`,
  MANAGED_RISK: WIDGET_TYPE.MANAGED_RISK,
  MANAGED_SME_RISK: WIDGET_TYPE.MANAGED_SME_RISK,
  NATIVE_KYC: WIDGET_TYPE.NATIVE_KYC,
  MANAGED_KYC: WIDGET_TYPE.MANAGED_KYC,
  MANAGED_SME_KYC: WIDGET_TYPE.MANAGED_SME_KYC,
  RECOLLECTION_KYC: WIDGET_TYPE.RECOLLECTION_KYC,
  UNMANAGED_KYC: WIDGET_TYPE.UNMANAGED_KYC,
  DOCUMENT_UPLOAD: WIDGET_TYPE.DOCUMENT_UPLOAD,
  ENABLE_PAYOUTS: APP_PATHS.ENABLE_PAYOUTS_PATH,
  CELEBRATION: 'celebration-flow',
  WHATS_NEXT: APP_PATHS.WHATS_NEXT_PATH,
  SIGNUP: 'signup',
  MERCHANT_VERIFICATION: WIDGET_TYPE.MERCHANT_VERIFICATION,
};

const shouldRenderAMLWidget = (isUnmanagedKyc, isWidget, merchant) => {
  if (!isWidget || isUnmanagedKyc) {
    return false;
  }

  const { status } = merchant;
  const currentStatusValue = ONBOARDING_STATUS_VALUES_V2[status];

  return (
    (currentStatusValue >= ONBOARDING_STATUS_VALUES_V2.NOT_STARTED &&
      currentStatusValue <= ONBOARDING_STATUS_VALUES_V2.TRANSACTION_DETAILS_STARTED) ||
    (currentStatusValue >= ONBOARDING_STATUS_VALUES_V2.TRANSACTIONS_ENABLED &&
      currentStatusValue <= ONBOARDING_STATUS_VALUES_V2.PAYOUT_DETAILS_STARTED)
  );
};

const shouldRenderKYCFlow = ({ status }) => {
  const currentStatusValue = ONBOARDING_STATUS_VALUES_V2[status];

  return (
    currentStatusValue >= ONBOARDING_STATUS_VALUES_V2.NOT_STARTED &&
    currentStatusValue < ONBOARDING_STATUS_VALUES_V2.TRANSACTIONS_REVIEW_AUTOMATED
  );
};

const shouldRenderCelebrationFlow = (isWidget, merchant, isUnmanagedKyc) => {
  const { country, status, timestamps } = merchant;
  const currentStatusValue = ONBOARDING_STATUS_VALUES_V2[status];

  const widgetFlowCondition =
    (currentStatusValue >= ONBOARDING_STATUS_VALUES_V2.TRANSACTIONS_REVIEW_AUTOMATED &&
      currentStatusValue <= ONBOARDING_STATUS_VALUES_V2.TRANSACTIONS_ENABLED) ||
    (currentStatusValue === ONBOARDING_STATUS_VALUES_V2.COMPLETED &&
      differenceInSeconds(new Date(), new Date(timestamps.payoutsRequestedAt)) <
        celebrationTimeWindow);

  const unmanagedFlowCondition =
    (currentStatusValue >= ONBOARDING_STATUS_VALUES_V2.TRANSACTIONS_REVIEW_AUTOMATED &&
      currentStatusValue <= ONBOARDING_STATUS_VALUES_V2.TRANSACTIONS_REVIEW_MANUAL) ||
    (currentStatusValue === ONBOARDING_STATUS_VALUES_V2.TRANSACTIONS_ENABLED &&
      differenceInSeconds(new Date(), new Date(timestamps.transactionsRequestedAt)) <
        celebrationTimeWindow);

  return isWidget && (isUnmanagedKyc || country !== 'GB')
    ? widgetFlowCondition
    : unmanagedFlowCondition;
};

const fallbackToAfterFlowStep = ({ isUnmanagedKyc, isWidget }) => {
  const afterFlowStep = getAfterFlowStep({ isUnmanagedKyc, isWidget });
  return fallbackTo(afterFlowStep);
};

const routeMapper = {
  [FLOW_IDS.MERCHANT_VERIFICATION]: {
    config: merchantVerificationFlow,
    condition: ({ widgetType }) => widgetType === WIDGET_TYPE.MERCHANT_VERIFICATION,
  },
  [FLOW_IDS.RECOLLECTION_KYC]: {
    config: recollectionFlow,
    condition: ({ widgetType }) => widgetType === WIDGET_TYPE.RECOLLECTION_KYC,
  },
  [FLOW_IDS.SUBMITTED_MANAGED_RISK]: {
    config: preAssessmentOverviewFlow,
    condition: ({ widgetType, showOverview }) =>
      (widgetType === WIDGET_TYPE.MANAGED_RISK || widgetType === WIDGET_TYPE.MANAGED_SME_RISK) &&
      showOverview,
  },
  [FLOW_IDS.WRONG_PREFILL]: {
    config: preAssessmentWrongPrefillFlow,
    condition: ({ widgetType, merchantError }) =>
      widgetType === WIDGET_TYPE.MANAGED_RISK &&
      merchantError &&
      merchantError.errorCode === 'MOMA_SALESFORCE_INVALID_PREFILL_DATA',
  },
  [FLOW_IDS.MANAGED_RISK]: {
    config: preAssessmentNonSmeFlow,
    condition: ({ widgetType, showOverview, merchantError }) =>
      widgetType === WIDGET_TYPE.MANAGED_RISK &&
      !showOverview &&
      (merchantError === undefined ||
        merchantError.errorCode !== 'MOMA_SALESFORCE_INVALID_PREFILL_DATA'),
  },
  [FLOW_IDS.MANAGED_SME_RISK]: {
    config: preAssessmentSmeFlow,
    condition: ({ widgetType, showOverview, merchantError }) =>
      widgetType === WIDGET_TYPE.MANAGED_SME_RISK && !showOverview,
  },
  [FLOW_IDS.NATIVE_KYC]: {
    config: merchantApplicationFlow,
    condition: ({ merchant, isUnmanagedKyc, isWidget, widgetType }) =>
      widgetType === WIDGET_TYPE.NATIVE_KYC &&
      shouldRenderAMLWidget(isUnmanagedKyc, isWidget, merchant),
  },
  [FLOW_IDS.MANAGED_KYC]: {
    config: merchantApplicationFlow,
    condition: ({ merchant, widgetType }) =>
      widgetType === WIDGET_TYPE.MANAGED_KYC && shouldRenderKYCFlow(merchant),
  },
  [FLOW_IDS.MANAGED_SME_KYC]: {
    config: merchantApplicationFlow,
    condition: ({ merchant, widgetType }) =>
      widgetType === WIDGET_TYPE.MANAGED_SME_KYC && shouldRenderKYCFlow(merchant),
  },
  [FLOW_IDS.UNMANAGED_KYC]: {
    config: merchantApplicationFlow,
    condition: ({ merchant, isUnmanagedKyc }) => isUnmanagedKyc && shouldRenderKYCFlow(merchant),
  },
  [FLOW_IDS.DOCUMENT_UPLOAD]: {
    config: documentUploadFlow,
    condition: ({ widgetType }) => widgetType === WIDGET_TYPE.DOCUMENT_UPLOAD,
  },
  [FLOW_IDS.CELEBRATION]: {
    config: celebrationFlow,
    condition: ({ merchant, isWidget, isUnmanagedKyc }) =>
      shouldRenderCelebrationFlow(isWidget, merchant, isUnmanagedKyc),
  },
  [FLOW_IDS.ENABLE_PAYOUTS]: {
    config: enablePayoutsConfig,
    condition: ({ merchant, isWidget }) => {
      const { status } = merchant;
      const currentStatusValue = ONBOARDING_STATUS_VALUES_V2[status];
      return !isWidget && currentStatusValue >= ONBOARDING_STATUS_VALUES_V2.TRANSACTIONS_ENABLED;
    },
  },
  [FLOW_IDS.WHATS_NEXT]: {
    config: fallbackToAfterFlowStep,
    condition: ({ merchant }) => {
      const { status } = merchant;
      const currentStatusValue = ONBOARDING_STATUS_VALUES_V2[status];
      return currentStatusValue > ONBOARDING_STATUS_VALUES_V2.PAYOUT_DETAILS_STARTED;
    },
  },
  [FLOW_IDS.SIGNUP]: {
    config: signupFlow,
    condition: ({ merchant = { status } }) => {
      return IS_ONBOARDING_APP && !status;
    },
  },
};

export const router = ({
  features,
  isUnmanagedKyc,
  isWidget,
  merchant,
  merchantError,
  preAssessmentDetails,
  questionnaires,
  region,
  widgetType,
  merchantCountryConfig,
}) => {
  const showOverview = _get(preAssessmentDetails, 'showOverview', false);

  const flow = Object.values(routeMapper).find(({ condition }) =>
    condition({
      features,
      isUnmanagedKyc,
      isWidget,
      merchant,
      merchantError,
      showOverview,
      widgetType,
    }),
  );

  const options = {
    features,
    isUnmanagedKyc,
    isWidget,
    merchant,
    widgetType,
    region,
    questionnaires,
    merchantCountryConfig,
  };

  return flow && flow.config ? flow.config(options) : fallbackToAfterFlowStep(options);
};
